import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import Button from '../shared/Button';
import { Link } from 'react-router-dom';

// Animation variants
const sectionVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: {
      staggerChildren: 0.3
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.8,
      ease: "easeOut"
    }
  }
};

export default function LoyaltySection() {
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const videoContainerRef = useRef(null);

  // Lazy load video when section comes into view
  useEffect(() => {
    if (!videoContainerRef.current || shouldLoadVideo) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShouldLoadVideo(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin: '200px', threshold: 0 }
    );

    observer.observe(videoContainerRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <section className="relative overflow-hidden bg-white py-16">
      <div className="container mx-auto">
        <div className="flex flex-col md:flex-row">
          {/* Left side with text and icons - 40% width */}
          <div className="w-full md:w-2/5 p-6 md:p-12 flex flex-col justify-center">
            <motion.div
              variants={sectionVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="space-y-6"
            >
              <motion.h2 variants={itemVariants} className="text-4xl md:text-5xl lg:text-6xl font-heading text-brand-navy">
                JOIN OUR LOYALTY PROGRAM
              </motion.h2>
              <motion.p variants={itemVariants} className="text-lg text-brand-navy mb-8">
                Earn rewards every time you dine with us! Our loyalty program lets you earn points on every purchase that can be redeemed for $5, $10, or $20 off your meal.
              </motion.p>

              {/* Loyalty Steps */}
              <div className="relative">
                {/* Vertical line connecting the circles */}
                <div className="absolute left-[36px] top-[36px] w-[2px] h-[calc(100%-72px)] bg-brand-navy"></div>
                
                {/* Step 1 */}
                <motion.div variants={itemVariants} className="flex items-center mb-16 relative">
                  <div className="min-w-[72px] w-[72px] h-[72px] rounded-full border-2 border-brand-navy flex items-center justify-center bg-white z-10 flex-shrink-0">
                    <img src="/icons/mobile.svg" alt="Mobile phone" className="w-8 h-8" />
                  </div>
                  <div className="ml-6">
                    <p className="text-lg font-medium text-brand-navy">
                      Give your phone number each time you visit an OPH location
                    </p>
                  </div>
                </motion.div>
                
                {/* Step 2 */}
                <motion.div variants={itemVariants} className="flex items-center mb-16 relative">
                  <div className="min-w-[72px] w-[72px] h-[72px] rounded-full border-2 border-brand-navy flex items-center justify-center bg-white z-10 flex-shrink-0">
                    <img src="/icons/dollar-sign.svg" alt="Dollar sign" className="w-8 h-8" />
                  </div>
                  <div className="ml-6">
                    <p className="text-lg font-medium text-brand-navy">
                      Earn 1 point for every dollar you spend on your favorite breakfast items
                    </p>
                  </div>
                </motion.div>
                
                {/* Step 3 */}
                <motion.div variants={itemVariants} className="flex items-center relative">
                  <div className="min-w-[72px] w-[72px] h-[72px] rounded-full border-2 border-brand-navy flex items-center justify-center bg-white z-10 flex-shrink-0">
                    <img src="/icons/pancake.svg" alt="Pancake stack" className="w-8 h-8" />
                  </div>
                  <div className="ml-6">
                    <p className="text-lg font-medium text-brand-navy">
                      Redeem 75 points for $5 off, 150 for $10 off, or 250 for $20 off — anytime
                    </p>
                  </div>
                </motion.div>
              </div>
              
              {/* Sign Up Button under icon stack */}
              <motion.div variants={itemVariants} className="flex flex-wrap gap-4 mt-8">
                <Button 
                  as={Link}
                  to="/loyalty" 
                  variant="solid" 
                  color="navy"
                  icon={
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  }
                >
                  Sign up for rewards
                </Button>
              </motion.div>
            </motion.div>
          </div>
          
          {/* Right side with video - 60% width with border radius on left */}
          <div ref={videoContainerRef} className="w-full md:w-3/5 md:absolute md:right-0 md:top-0 md:bottom-0 relative">
            <div className="h-full">
              {shouldLoadVideo ? (
                <video
                  src="/oph-hero_1.mp4"
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full h-full object-cover md:rounded-l-lg"
                />
              ) : (
                // Placeholder while video loads
                <div className="w-full h-full bg-brand-cream md:rounded-l-lg" />
              )}
              
              {/* Learn More button overlaid on the image (hidden on mobile) */}
              <div className="absolute bottom-8 left-8 md:bottom-12 md:left-12 z-10 hidden md:flex flex-wrap gap-4">
                <motion.div
                  variants={itemVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                >
                  <Button 
                    as={Link}
                    to="/loyalty" 
                    variant="solid" 
                    color="cream"
                    icon={
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    }
                  >
                    Join Loyalty Program
                  </Button>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}